import { IEnum, KeyOfEnum, TDate } from "@utils/TypeHelpers";
import { EnumContratoClienteStatus } from "./ContratoClienteStatus";
import { AfterLoadEventHandler, CreateOptionsParams, ISpecializedCombo, useCombo } from "@hooks/useCombo";
import React from "react";
import { MenuItem } from "@material-ui/core";
import { useContratoClienteService } from "@services/ContratoClienteService";
import { useThemeColor } from "@themes/Colors";

export enum EnumOrigemCancelamento{ 
    None = 0,
    PedidoCliente = 1,
    Inadimplencia = 2,
    NovoContrato = 3
}

export enum EnumTipoCobrancaChip { 
    None = 0,
    ProximaFatura = 1,
    Caixa = 2,
}

export interface IContratoCliente {
    contratoClienteId: number

    contratoClienteStatusId: EnumContratoClienteStatus
    linhaId: number
    clienteId: number
    vendedorId?: number
    vendedorNome?: string

    referencia: string
    notas: string

    inicio: Date | null
    termino?: Date

    franquiaGb: number
    valorPlano: number
    valorAssessoria: number
    valorChip: number
    tipoCobrancaChip: EnumTipoCobrancaChip
    valorChipDescontado: number

    cobrarInteira: string
    usuarioLinha: string

    justificativaCancelamento: string
    dataCancelamento: TDate
    usuarioCancelamentoNome: string

    clienteNome: string
    clienteCpf: string
    clienteRemetente: string
    empresaNome: string
    linhaNumero: string
    linhaNumeroContrato: string
    
    contratoClienteStatusNome: string
    linhaContratoOperadoraOperadoraNome: string
    linhaContratoOperadoraEmpresaNome: string

    origemCancelamento: EnumOrigemCancelamento
    origemCancelamentoDescription: string

}

export type IComboOrigemCancelamento = ISpecializedCombo<IEnum<EnumOrigemCancelamento>>;

export function useOrigemCancelamentoCombo () : IComboOrigemCancelamento {
    const combo = useCombo<IEnum<EnumOrigemCancelamento>>();

    const contratoClienteService = useContratoClienteService();

    const createOptions = React.useCallback((params?: CreateOptionsParams) => {
        return combo.set.map((enumerator, index) => {
            return <MenuItem key={index} value={enumerator.value}>{params?.prefix}{enumerator.description}{params?.sufix}</MenuItem>
        });
    }, [combo.set]);

    const loadOptions = React.useCallback((afterLoad?: AfterLoadEventHandler<IEnum<EnumOrigemCancelamento>>) => {
        combo.load(contratoClienteService.listarOrigens, afterLoad)
    }, [combo, contratoClienteService.listarOrigens]);

    return React.useMemo(() => ({
        ...combo, createOptions, loadOptions
    }), [combo, createOptions, loadOptions]);
}

export function useOrigemCancelamentoColor () {
    const themeColor = useThemeColor();

    const map : KeyOfEnum<EnumOrigemCancelamento, string> = React.useMemo(() => ({
        [EnumOrigemCancelamento.None]: themeColor.status.cinza,
        [EnumOrigemCancelamento.PedidoCliente]: themeColor.status.verde,
        [EnumOrigemCancelamento.Inadimplencia]: themeColor.status.vermelho,
        [EnumOrigemCancelamento.NovoContrato]: themeColor.status.azul,
    }), [themeColor.status.azul, themeColor.status.cinza, themeColor.status.verde, themeColor.status.vermelho]);

    return React.useMemo(() => map, [map]);
}

export type IComboTipoCobrancaChip = ISpecializedCombo<IEnum<EnumTipoCobrancaChip>>;

export function useTipoCobrancaChipCombo () : IComboTipoCobrancaChip {
    const combo = useCombo<IEnum<EnumTipoCobrancaChip>>();

    const contratoClienteService = useContratoClienteService();

    const createOptions = React.useCallback((params?: CreateOptionsParams) => {
        return combo.set.map((enumerator, index) => {
            return <MenuItem key={index} value={enumerator.value}>{params?.prefix}{enumerator.description}{params?.sufix}</MenuItem>
        });
    }, [combo.set]);

    const loadOptions = React.useCallback((afterLoad?: AfterLoadEventHandler<IEnum<EnumTipoCobrancaChip>>) => {
        combo.load(contratoClienteService.listarTipoCobrancaChip, afterLoad)
    }, [combo, contratoClienteService.listarTipoCobrancaChip]);

    return React.useMemo(() => ({
        ...combo, createOptions, loadOptions
    }), [combo, createOptions, loadOptions]);
}
